import React from "react";
import { useTranslation } from "react-i18next";
import Input from "components/input";
import PropTypes from "prop-types";
import Select from "components/select";
import { ReactComponent as BankDetailsIcon } from "images/profile/bank-details-icon.svg";
import { ReactComponent as UreedIcon } from "images/profile/ureed-icon.svg";
import { ReactComponent as PayoneerIcon } from "images/profile/payoneer-icon.svg";
import { ReactComponent as ExpressIcon } from "images/profile/express-icon.svg";
import { useLookupsContext } from "hooks/useContext";
import VatExempt from "pages/profile/common/vatExempt";
import Warning from "pages/profile/common/warning";
import {
  PREFERRED_CURRENCY_USD_ID,
  SHOW_VENDOR_PREFERRED_CURRENCY_UI,
} from "utils/constantValues";

function BankDetailsForm({
  register = () => {},
  setValue = () => {},
  errors = {},
  watch = () => {},
  getValues = () => {},
  title = "",
  containerClassName = "flex p-[24px] flex-col gap-[30px] self-stretch  rounded-[8px] border-[1px] border-solid border-default-color mt-[10px]",
  userDetails,
}) {
  const [t] = useTranslation();
  const lookups = useLookupsContext(true);
  const countries = lookups.countries ?? [];
  const currenciesOptions = SHOW_VENDOR_PREFERRED_CURRENCY_UI
    ? (lookups.currencies ?? []).map((elem) => ({
        ...elem,
        value: elem.id,
        label: elem.code,
      }))
    : [];

  const isUreed = !!watch("ureed");
  const isPayoneer = !!watch("payoneer");
  const isCashExpress =
    !!watch("cashExpressPhoneNumber") || !!watch("fullName");

  const isBankDetails = watch([
    "accountName",
    "bankName",
    "accountType",
    "accountNumber",
    "swiftCode",
    "iban",
    "sortCode",
  ]).every((elem) => !!elem);
  const isBankDetailsRequired = !isUreed && !isCashExpress && !isPayoneer;
  const isUreedRequired = !isPayoneer && !isCashExpress && !isBankDetails;
  const isPayoneerRequired = !isUreed && !isCashExpress && !isBankDetails;
  const isCashExpressRequired = !isBankDetails && !isUreed && !isPayoneer;
  return (
    <div className={containerClassName}>
      <div className="grid items-center w-full">
        <div className="text-primary font-semibold text-[30px] not-italic">
          {title || t("financialInformation")}
        </div>

        <div className="text-gray-500 text-[18px] not-italic">
          {t("financialInformationSubTitle")}
        </div>
      </div>

      <Warning message={t("financialDetailsWarning")} className="my-[-15px]" />

      {SHOW_VENDOR_PREFERRED_CURRENCY_UI ? (
        <Select
          label={t("preferredCurrency")}
          placeholder={t("placeholderSelect", {
            field: t("preferredCurrency").toLowerCase(),
          })}
          name={"preferredCurrency"}
          containerClassName="flex flex-1 flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          isClearable={false}
          value={
            watch("preferredCurrency")
              ? currenciesOptions.find(
                  (elem) => elem.value === watch("preferredCurrency")
                )
              : currenciesOptions.find(
                  (elem) => elem.value === PREFERRED_CURRENCY_USD_ID
                )
          }
          onChange={(e) => {
            setValue("preferredCurrency", e.value);
          }}
          isRequired={false}
          register={register}
          options={currenciesOptions}
        />
      ) : null}

      {/* <<<<<Vat Exempt section start>>>> */}

      <VatExempt
        countries={countries}
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        userDetails={userDetails}
      />
      {/* <<<<<Vat Exempt section end>>>> */}

      {/* <<<<<Bank Details section start>>>> */}

      <div className="flex flex-col py-[20px] px-[24px] items-center gap-[18px] rounded-[6px] border-[1px] border-solid border-primary">
        <div className="flex items-center w-full gap-[12px] ">
          <BankDetailsIcon />
          <div className="text-base-black font-semibold text-[20px] not-italic">
            {t("bankDetails")}{" "}
            <span className="text-gray-500 font-semibold text-[20px] not-italic ">{`(${t(
              "Primary"
            )})`}</span>
          </div>
        </div>

        <div className="border-b-[1px] border-solid border-default-color w-calc-100-plus-48" />
        <div className="flex flex-wrap gap-[20px]">
          <div className="grid grid-cols-2 sm-max:grid-cols-1  w-full gap-[16px]">
            <Input
              label={t("beneficiaryName")}
              isRequired={isBankDetailsRequired}
              placeholder={t("placeHolderType", {
                title: t("beneficiaryName").toLowerCase(),
              })}
              name="accountName"
              register={register}
              registerArgs={{
                required: isBankDetailsRequired
                  ? t("errorField", { field: t("accountName") })
                  : false,
              }}
              errorMessage={
                errors.accountName && isBankDetailsRequired
                  ? errors.accountName.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.accountName && isBankDetailsRequired
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />

            <Input
              label={t("bankName")}
              isRequired={isBankDetailsRequired}
              placeholder={t("placeHolderType", {
                title: t("bankName").toLowerCase(),
              })}
              name="bankName"
              register={register}
              registerArgs={{
                required: isBankDetailsRequired
                  ? t("errorField", {
                      field: t("bankName"),
                    })
                  : false,
              }}
              errorMessage={
                errors.bankName && isBankDetailsRequired
                  ? errors.bankName.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.bankName && isBankDetailsRequired
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />
          </div>

          <div className="grid grid-cols-2 sm-max:grid-cols-1  w-full gap-[16px]">
            <Input
              label={t("accountType")}
              isRequired={isBankDetailsRequired}
              placeholder={t("placeHolderType", {
                title: t("accountType").toLowerCase(),
              })}
              name="accountType"
              register={register}
              registerArgs={{
                required: isBankDetailsRequired
                  ? t("errorField", { field: t("accountType") })
                  : false,
              }}
              errorMessage={
                errors.accountType && isBankDetailsRequired
                  ? errors.accountType.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.accountType && isBankDetailsRequired
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />

            <Input
              label={t("accountNumber")}
              isRequired={isBankDetailsRequired}
              placeholder={t("placeHolderType", {
                title: t("accountNumber").toLowerCase(),
              })}
              name="accountNumber"
              register={register}
              registerArgs={{
                required: isBankDetailsRequired
                  ? t("errorField", {
                      field: t("accountNumber"),
                    })
                  : false,
              }}
              errorMessage={
                errors.accountNumber && isBankDetailsRequired
                  ? errors.accountNumber.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.accountNumber && isBankDetailsRequired
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />
          </div>

          <Input
            label={t("swiftCode")}
            isRequired={isBankDetailsRequired}
            placeholder={t("placeHolderType", {
              title: t("swiftCode").toLowerCase(),
            })}
            name="swiftCode"
            register={register}
            registerArgs={{
              required: isBankDetailsRequired
                ? t("errorField", {
                    field: t("swiftCode"),
                  })
                : false,
            }}
            errorMessage={
              errors.swiftCode && isBankDetailsRequired
                ? errors.swiftCode.message
                : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.swiftCode && isBankDetailsRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("sortCode")}
            isRequired={isBankDetailsRequired}
            placeholder={t("placeHolderType", {
              title: t("sortCode").toLowerCase(),
            })}
            name="sortCode"
            register={register}
            registerArgs={{
              required: isBankDetailsRequired
                ? t("errorField", {
                    field: t("sortCode"),
                  })
                : false,
            }}
            errorMessage={
              errors.sortCode && isBankDetailsRequired
                ? errors.sortCode.message
                : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.sortCode && isBankDetailsRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
          <Input
            label={t("iban")}
            isRequired={isBankDetailsRequired}
            placeholder={t("placeHolderType", {
              title: t("iban").toLowerCase(),
            })}
            name="iban"
            register={register}
            registerArgs={{
              required: isBankDetailsRequired
                ? t("errorField", {
                    field: t("iban"),
                  })
                : false,
            }}
            errorMessage={
              errors.iban && isBankDetailsRequired ? errors.iban.message : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.iban && isBankDetailsRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        </div>
      </div>
      {/* <<<<<Bank Details section end>>>> */}

      {/* <<<<<UREED section start>>>> */}

      <div className="flex flex-col py-[20px] px-[24px] items-center gap-[18px] rounded-[6px] border-[1px] border-solid border-primary">
        <div className="flex items-center w-full gap-[12px] ">
          <UreedIcon />
   
        </div>

        <div className="border-b-[1px] border-solid border-default-color w-calc-100-plus-48" />
        <div className="grid w-full gap-[20px]">
          <Input
            label={t("ureedLabel")}
            isRequired={isUreedRequired}
            placeholder={t("ureedPlaceholder")}
            name="ureed"
            register={register}
            registerArgs={{
              required: isUreedRequired
                ? t("errorField", {
                    field: t("ureed"),
                  })
                : false,
            }}
            errorMessage={
              errors.ureed && isUreedRequired ? errors.ureed.message : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.ureed && isUreedRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        </div>
      </div>
      {/* <<<<<UREED section end>>>> */}
      {/* <<<<<PAYONEER section start>>>> */}

      <div className="flex flex-col py-[20px] px-[24px] items-center gap-[18px] rounded-[6px] border-[1px] border-solid border-primary">
        <div className="flex items-center w-full gap-[12px] ">
          <PayoneerIcon />
          <div className="text-base-black font-semibold text-[20px] not-italic">
            {t("payoneer")}
          </div>
        </div>

        <div className="border-b-[1px] border-solid border-default-color w-calc-100-plus-48" />
        <div className="grid w-full gap-[20px]">
          <Input
            label={t("payoneerLabel")}
            isRequired={isPayoneerRequired}
            placeholder={t("payoneerPlaceholder")}
            name="payoneer"
            register={register}
            registerArgs={{
              required: isPayoneerRequired
                ? t("errorField", {
                    field: t("payoneer"),
                  })
                : false,
            }}
            errorMessage={
              errors.payoneer && isPayoneerRequired ? errors.payoneer.message : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.payoneer && isPayoneerRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        </div>
      </div>
      {/* <<<<<PAYONEER section end>>>> */}

      {/* <<<<<Cash express section start>>>> */}
      <div className="flex flex-col py-[20px] px-[24px] items-center gap-[18px] rounded-[6px] border-[1px] border-solid border-primary">
        <div className="flex items-center w-full gap-[12px] ">
          <ExpressIcon />
          <div className="text-base-black font-semibold text-[20px] not-italic">
            {t("cashExpress")}
          </div>
        </div>

        <div className="border-b-[1px] border-solid border-default-color w-calc-100-plus-48" />
        <div className="grid w-full gap-[20px]">
          <div className="grid gap-[6px] w-full">
            <Input
              label={t("fullName")}
              isRequired={isCashExpressRequired}
              placeholder={t("placeHolderType", {
                title: t("fullName").toLowerCase(),
              })}
              name="fullName"
              register={register}
              registerArgs={{
                required: isCashExpressRequired
                  ? t("errorField", {
                      field: t("fullName"),
                    })
                  : false,
              }}
              errorMessage={
                errors.fullName && isCashExpressRequired
                  ? errors.fullName.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.fullName && isCashExpressRequired
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />
            <div className="text-gray-500 font-regular text-[14px] not-italic">
              {t("expressFullNameSubTitle")}
            </div>
          </div>

          <Input
            label={t("phoneNumber")}
            isRequired={isCashExpressRequired}
            placeholder={t("phonePlaceholder")}
            name="cashExpressPhoneNumber"
            type="text"
            register={register}
            registerArgs={{
              required: isCashExpressRequired
                ? t("errorField", { field: t("phoneNumber") })
                : false,
            }}
            errorMessage={
              errors.cashExpressPhoneNumber && isCashExpressRequired
                ? errors.cashExpressPhoneNumber.message
                : ""
            }
            containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
              errors.cashExpressPhoneNumber && isCashExpressRequired
                ? "border-red"
                : "border-default-color"
            } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
          />
        </div>
      </div>
      {/* <<<<<Cash express section end>>>> */}
    </div>
  );
}

BankDetailsForm.propTypes = {
  register: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  watch: PropTypes.func,
  getValues: PropTypes.func,
  title: PropTypes.string,
  containerClassName: PropTypes.string,
};

export default BankDetailsForm;
